import React, {useState, useEffect, useRef, useCallback} from 'react';
import {Box, IconButton, TextField, Typography, Tooltip} from '@mui/material';
import ChatBubble from '@Pages/ChatBot/ChatBubble';
import {useDispatch, useSelector} from 'react-redux';
import {addMessage, removeMessage} from '@Redux/chatSlice'; // Import the addMessage action
import {checkAndHandleStorageOverflow} from '@Utils/storageUtils'; // Import the storage overflow handler
import axiosInstance from "@API/axios";
import {store} from '@Redux/store';
import {v4 as uuidv4} from 'uuid';
import {format} from 'date-fns';
import ChatBotInterfaceStyles from './ChatBotInterfaceStyles';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import ThemeCreator from '@Themes/themeCreator';
import RobotIcon from '@Assets/RobotIcon';
import {AI_CHAT_ASK_URL} from "../../API/APIRoutes";
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import StopCircleRoundedIcon from '@mui/icons-material/StopCircleRounded';

const ChatbotInterface = ({popupWidth, isFullScreen = false}) => {
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [visibleMessagesCount, setVisibleMessagesCount] = useState(10);
    const [currentUserId, setCurrentUserId] = useState(null);
    const userId = useSelector((state) => state.user.userId);
    const messages = useSelector((state) => state.chat.userChats[userId]) || [];
    const dispatch = useDispatch();
    const messagesEndRef = useRef(null);
    const chatContainerRef = useRef(null);
    const theme = ThemeCreator();
    const initialMessage = "Hello I am UniServe 🧠, your Unilever Clearance AI Assistant. How can I help you today?";
    const [speakingMessageId, setSpeakingMessageId] = useState(null);

    // Update visible count when messages increase
    useEffect(() => {
        if (messages.length > visibleMessagesCount) {
            setVisibleMessagesCount(messages.length);
        }
    }, [messages.length]);

    // Handle user changes - reset interface when user switches
    useEffect(() => {
        if (userId && userId !== currentUserId) {

            // Stop any ongoing speech
            window.speechSynthesis.cancel();
            setSpeakingMessageId(null);

            // Reset interface state for new user
            setInput('');
            setLoading(false);
            setVisibleMessagesCount(10);
            setCurrentUserId(userId);
        }
    }, [userId, currentUserId]);

    // Initialize chat for current user - ensure initial message is always present
    useEffect(() => {
        if (userId && messages.length === 0) {
            dispatch(addMessage({
                userId,
                message: {
                    id: uuidv4(),
                    text: initialMessage,
                    sender: 'bot',
                    timestamp: new Date().toISOString(),
                }
            }));
        } else if (userId && messages.length > 0) {
            // Check if initial message exists, if not add it
            const hasInitialMessage = messages.some(msg =>
                msg.sender === 'bot' && msg.text === initialMessage
            );
            if (!hasInitialMessage) {
                dispatch(addMessage({
                    userId,
                    message: {
                        id: uuidv4(),
                        text: initialMessage,
                        sender: 'bot',
                        timestamp: new Date().toISOString(),
                    }
                }));
            }
        }
    }, [userId, messages.length, dispatch]);

    useEffect(() => {
        // More aggressive voice loading
        const loadVoices = () => {
            const voices = window.speechSynthesis.getVoices();
            if (voices.length > 0) {
            } else {
                // Try to force voice loading
                const tempUtterance = new SpeechSynthesisUtterance('');
                window.speechSynthesis.speak(tempUtterance);
                window.speechSynthesis.cancel();
            }
        };

        // Load immediately
        loadVoices();

        // Set up event listener
        window.speechSynthesis.onvoiceschanged = loadVoices;

        // Force voice loading after a short delay
        setTimeout(loadVoices, 100);
        setTimeout(loadVoices, 500);

        return () => {
            window.speechSynthesis.onvoiceschanged = null;
        };
    }, []);
useEffect(() => {
    // Handle page refresh/navigation
    const handleBeforeUnload = () => {
        window.speechSynthesis.cancel();
        setSpeakingMessageId(null);
    };

    // Add event listener for page refresh/navigation
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
        // Stop speech when component unmounts (chat popup closes)
        window.speechSynthesis.cancel();
        setSpeakingMessageId(null);
        // Remove the event listener
        window.removeEventListener('beforeunload', handleBeforeUnload);
    };
}, []);

// Add this state for tracking retry functionality
    const [isRetrying, setIsRetrying] = useState(false);

// Shared function for making API requests with loading states
    const makeApiRequest = async (questionText, isRetry = false) => {
        const loadingId = uuidv4();

        if (isRetry) {
            setIsRetrying(true);
        }

        setLoading(true);
        dispatch(addMessage({
            userId,
            message: {
                id: loadingId,
                text: '🧠 Thinking...',
                sender: 'loading',
            }
        }));

        // Timer logic
        const firstTimer = setTimeout(() => {
            const currentMessages = store.getState().chat.userChats[userId] || [];
            const loadingMessageExists = currentMessages.some(msg => msg.id === loadingId);

            if (loadingMessageExists) {
                dispatch(removeMessage({userId, messageId: loadingId}));
                dispatch(addMessage({
                    userId,
                    message: {
                        id: loadingId,
                        text: '⏳ Still thinking... Just a moment longer!',
                        sender: 'loading',
                    }
                }));
            }
        }, 10000);

        const secondTimer = setTimeout(() => {
            const currentMessages = store.getState().chat.userChats[userId] || [];
            const loadingMessageExists = currentMessages.some(msg => msg.id === loadingId);

            if (loadingMessageExists) {
                dispatch(removeMessage({userId, messageId: loadingId}));
                dispatch(addMessage({
                    userId,
                    message: {
                        id: loadingId,
                        text: '🕐 This is taking longer than usual...\n' +
                            'The server may be handling a high number of requests or is temporarily under heavy load. Thanks for your patience!',
                        sender: 'loading',
                    }
                }));
            }
        }, 40000);

        try {
            const response = await axiosInstance.post(AI_CHAT_ASK_URL, {
                question: questionText
            });

            const data = response.data;
            clearTimeout(firstTimer);
            clearTimeout(secondTimer);
            dispatch(removeMessage({userId, messageId: loadingId}));

            const messagesLength = store.getState().chat.userChats[userId]?.length || 0;
            const isChatCleared = messagesLength <= 1;

            if (!isChatCleared) {
                dispatch(addMessage({
                    userId,
                    message: {
                        id: uuidv4(),
                        text: ` ${data.response}`,
                        sender: 'bot',
                    }
                }));
            }
        } catch (error) {
            clearTimeout(firstTimer);
            clearTimeout(secondTimer);
            dispatch(removeMessage({userId, messageId: loadingId}));
            console.error('Error fetching response from server:', error);
            dispatch(addMessage({
                userId,
                message: {
                    id: uuidv4(),
                    text: error.response.data.response,
                    sender: 'bot',
                }
            }));
        } finally {
            setLoading(false);
            if (isRetry) {
                setIsRetrying(false);
            }
        }

        checkAndHandleStorageOverflow(dispatch, userId);
    };

// Simplified handleSendMessage
    const handleSendMessage = async () => {
        const questionText = input.trim();

        dispatch(addMessage({
            userId,
            message: {
                id: uuidv4(),
                text: questionText,
                sender: 'user',
            }
        }));

        setInput('');
        await makeApiRequest(questionText);
    };

// Retry function
const handleRetry = async () => {
    // Stop any currently playing speech immediately
    window.speechSynthesis.cancel();
    setSpeakingMessageId(null);

    const lastUserMessage = messages.slice().reverse().find(msg => msg.sender === 'user');
    if (!lastUserMessage) return;

    // Find and remove the last bot message
    const lastBotMessage = messages.slice().reverse().find(msg => msg.sender === 'bot');
    if (lastBotMessage && lastBotMessage.text !== initialMessage) {
        dispatch(removeMessage({userId, messageId: lastBotMessage.id}));
    }

    await makeApiRequest(lastUserMessage.text, true);
};
const handleReadAloud = (text, messageId) => {
    // Stop any currently playing speech
    window.speechSynthesis.cancel();
    setSpeakingMessageId(null);

    const speakText = () => {
        // Get available voices with multiple attempts
        let voices = window.speechSynthesis.getVoices();

        // Priority order for most human-like voices (all free and unlimited)
        const voicePriority = [
            // Microsoft Edge premium voices (most natural)
            'Microsoft Aria Online (Natural) - English (United States)',
            'Microsoft Jenny Online (Natural) - English (United States)',
            'Microsoft Guy Online (Natural) - English (United States)',
            'Microsoft Ana Online (Natural) - English (United States)',
            // Chrome premium voices
            'Google US English',
            'Chrome OS US English',
            // macOS premium voices
            'Alex',
            'Samantha',
            'Karen',
            'Victoria',
            // Windows premium voices
            'Microsoft Zira Desktop - English (United States)',
            'Microsoft David Desktop - English (United States)',
            // Fallback to any neural/enhanced voice
            'Enhanced',
            'Neural',
            'Premium'
        ];

        // Find the best available voice
        let selectedVoice = null;
        for (const voiceName of voicePriority) {
            selectedVoice = voices.find(voice =>
                voice.name?.includes(voiceName) &&
                (voice.lang.startsWith('en-US') || voice.lang.startsWith('en-GB') || voice.lang === 'en')
            );
            if (selectedVoice) break;
        }

        // If no priority voice found, use best English voice available
        if (!selectedVoice) {
            selectedVoice = voices.find(voice =>
                (voice.lang.startsWith('en-US') || voice.lang.startsWith('en-GB')) &&
                !voice.localService
            ) || voices.find(voice => voice.lang.startsWith('en'));
        }

        // Clean markdown formatting and remove emojis
        const cleanedText = text
            // Remove emojis (comprehensive regex for all emoji ranges)
            .replace(/[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{1F700}-\u{1F77F}]|[\u{1F780}-\u{1F7FF}]|[\u{1F800}-\u{1F8FF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA00}-\u{1FA6F}]|[\u{1FA70}-\u{1FAFF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/gu, '')
            // Remove markdown tables (entire table blocks)
            .replace(/(\|.*\|.*\n)+/g, '')
            // Remove table header separators (like |---|---|)
            .replace(/^\s*\|[\s\-\|:]+\|\s*$/gm, '')
            // Remove any remaining pipe characters that might be table remnants
            .replace(/\|/g, ' ')
            // Remove markdown formatting
            .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold **text**
            .replace(/\*(.*?)\*/g, '$1') // Remove italic *text*
            .replace(/__(.*?)__/g, '$1') // Remove bold __text__
            .replace(/_(.*?)_/g, '$1') // Remove italic _text_
            .replace(/`(.*?)`/g, '$1') // Remove inline code `text`
            .replace(/```[\s\S]*?```/g, '') // Remove code blocks
            .replace(/#{1,6}\s*(.*)/g, '$1') // Remove headers # text
            .replace(/^\s*[-*+]\s+/gm, '') // Remove bullet points
            .replace(/^\s*\d+\.\s+/gm, '') // Remove numbered lists
            .replace(/\[(.*?)\]\(.*?\)/g, '$1') // Remove links [text](url)
            .replace(/!\[.*?\]\(.*?\)/g, '') // Remove images
            .replace(/~~(.*?)~~/g, '$1') // Remove strikethrough ~~text~~
            .replace(/>\s*/g, '') // Remove blockquotes
            .replace(/---+/g, '') // Remove horizontal rules
            // Clean up multiple spaces and empty lines
            .replace(/\s+/g, ' ')
            .replace(/\n\s*\n/g, '\n')
            .trim();

        // Create new speech utterance
        const utterance = new SpeechSynthesisUtterance(cleanedText);

        // Force English language
        utterance.lang = 'en-US';

        // Set the best voice if available
        if (selectedVoice) {
            utterance.voice = selectedVoice;
        }

        // Optimized settings for natural speech
        utterance.rate = 1; // Slightly slower for clarity
        utterance.pitch = 0.9; // Natural pitch
        utterance.volume = 0.9; // Comfortable volume

        // Track speech state
        let speechStarted = false;
        let speechEnded = false;

        // Add event listeners
        utterance.onstart = () => {
            speechStarted = true;
            setSpeakingMessageId(messageId);
        };



        utterance.onend = () => {
            speechEnded = true;
            setSpeakingMessageId(null);
        };

        utterance.onerror = (event) => {
            speechEnded = true;
            setSpeakingMessageId(null);
        };

        const cleanupSpeech = () => {
            if (!speechEnded) {
                speechEnded = true;
                setSpeakingMessageId(null);
            }
        };
        // Maximum duration safety cleanup (estimate 60ms per character)
        const maxDuration = Math.max(cleanedText.length *85, 10000); // Minimum 10 seconds
        setTimeout(() => {
            if (!speechEnded) {
                window.speechSynthesis.cancel();
                cleanupSpeech();
            }
        }, maxDuration);

        // Fallback mechanism for unreliable browsers
        setTimeout(() => {
            if (!speechStarted && window.speechSynthesis.speaking) {
                speechStarted = true;
                setSpeakingMessageId(messageId);
            }
        }, 300);

        // Simple monitoring without interference
        const monitorSpeech = () => {
            if (!speechEnded && !window.speechSynthesis.speaking) {
                speechEnded = true;
                setSpeakingMessageId(null);
            } else if (!speechEnded) {
                // Continue monitoring
                setTimeout(monitorSpeech, 500);
            }
        };

        // Start monitoring after speech should have started
        setTimeout(monitorSpeech, 1000);

        // Speak the text
        window.speechSynthesis.speak(utterance);
    };

    // Force voice loading with multiple strategies
    const ensureVoicesLoaded = (callback, attempt = 0) => {
        const voices = window.speechSynthesis.getVoices();

        if (voices.length > 0) {
            callback();
            return;
        }

        if (attempt >= 3) {
            console.warn('Voice loading timed out, proceeding with default voice');
            callback();
            return;
        }

        const dummyUtterance = new SpeechSynthesisUtterance('');
        window.speechSynthesis.speak(dummyUtterance);
        window.speechSynthesis.cancel();

        setTimeout(() => {
            ensureVoicesLoaded(callback, attempt + 1);
        }, 150);
    };

    ensureVoicesLoaded(speakText);
};
    const handleStopSpeech = () => {
        window.speechSynthesis.cancel();
        setSpeakingMessageId(null);
    };
    const handleKeyPress = (event) => {
        if (event.key === 'Enter' && !loading) {
            handleSendMessage();
        }
    };

    const handleScroll = useCallback(() => {
        if (chatContainerRef.current) {
            const scrollTop = chatContainerRef.current.scrollTop;
            const nearTopThreshold = 450;
            if (scrollTop < nearTopThreshold) {
                setVisibleMessagesCount(prevCount => Math.min(prevCount + 10, messages.length));
            }
        }
    }, [messages.length]);

    useEffect(() => {
        const timer = setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({behavior: 'instant'});
        }, 0);

        return () => clearTimeout(timer);
    }, [loading]);

    return (
        <ChatBotInterfaceStyles className="chatContainer" isDisabled={loading || input.trim() === ''}>
            <Box
                className="messagesContainer"
                ref={chatContainerRef}
                onScroll={handleScroll}
            >
                {messages.slice(-Math.max(visibleMessagesCount, messages.length))
                    .map((message, index, array) => {
                        // Check if this is the last bot message and not the initial greeting
                        const isLastBotMessage = message.sender === 'bot' &&
                            index === array.length - 1 &&
                            !loading &&
                            !isRetrying &&
                            message.text !== initialMessage;

                        // Check if this is any bot message (including initial greeting)
                        const isBotMessage = message.sender === 'bot';

                        return (
                            <React.Fragment key={message.id}>
                                {message.sender === 'bot' || message.sender === 'loading' ? (
                                    <Box className="chatBotMessageHeader">
                                        <RobotIcon scale={24}
                                                   color={theme.palette.white.main}
                                                   bgColor={theme.palette.primary.main}
                                                   isHappy={true}
                                                   isAnimated={false}/>
                                        <Typography className="chatBotMessageHeaderText">
                                            UniServe
                                        </Typography>
                                        <Typography className="dateHeaderText">
                                            {message.timestamp ? ` ${format(new Date(message.timestamp), 'EEE : hh:mm a')}` : ''}
                                        </Typography>
                                    </Box>
                                ) : null}
                                <ChatBubble
                                    text={message.text}
                                    sender={message.sender}
                                    popupWidth={popupWidth}
                                    isFullScreen={isFullScreen}
                                />
                                {isBotMessage && (
                                    <Box className="retryButtonContainer">
                                        {isLastBotMessage && (
                                            <Tooltip
                                                title="Retry – Still incorrect or not making sense? Try clearing the context first."
                                                placement="top"
                                                arrow
                                            >
                                                <IconButton
                                                    onClick={handleRetry}
                                                    className="retryButton"
                                                    size="small"
                                                >
                                                    <RefreshRoundedIcon fontSize="small"/>
                                                    <Typography variant="caption" sx={{ml: 0.5}}>
                                                        Retry
                                                    </Typography>
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        <Tooltip
                                            title={speakingMessageId === message.id ? "Stop audio" : "Read aloud - Audio may not be always available"}
                                            placement="top"
                                            arrow
                                        >
                                            <IconButton
                                                onClick={speakingMessageId === message.id ? handleStopSpeech : () => handleReadAloud(message.text, message.id)}
                                                className="retryButton"
                                                size="small"
                                                sx={{ml: isLastBotMessage ? 1 : 0}}
                                            >
                                                {speakingMessageId === message.id ? (
                                                    <StopCircleRoundedIcon fontSize="small"/>
                                                ) : (
                                                    <VolumeUpRoundedIcon fontSize="small"/>
                                                )}
                                                <Typography variant="caption" sx={{ml: 0.5}}>
                                                    {speakingMessageId === message.id ? 'Stop' : 'Listen'}
                                                </Typography>
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                )}
                            </React.Fragment>
                        );
                    })}
                <div ref={messagesEndRef}/>
            </Box>
            <Box className="inputContainer">
                <Box className="inputRow">
                    <TextField
                        variant="standard"
                        placeholder="Message Unilever Assistant"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyPress={(loading || input.trim() === '') ? null : handleKeyPress}
                        fullWidth
                        className="textField"
                        autoComplete="off"
                    />
                    <IconButton
                        color="primary"
                        onClick={handleSendMessage}
                        className="sendButton"
                        disabled={loading || input.trim() === ''}
                    >
                        <SendOutlinedIcon className="sendIcon"/>
                    </IconButton>
                </Box>
                <Typography className="aiNote">
                    AI can make mistakes. Please be precise with questions and verify important information.
                </Typography>

            </Box>

        </ChatBotInterfaceStyles>
    );
};

export default ChatbotInterface;
